import { ChangeDetectorRef, Component, computed, effect, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, debounceTime, finalize, map, of } from 'rxjs';
import { SelectChangeEvent } from 'primeng/select';
import { ConfirmationService } from 'primeng/api';
import _ from 'lodash';
import { EnumValues, EnumObjectExtended } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { AssetTypeIds } from '../../../models/asset/asset-type.constants';
import { ImportantLocations } from '../../../models/location';
import { QualityDefect } from '../../../models/inventory/quality-defects';
import { Roll } from '../../../models/production/roll';
import { RollQualityControlRequest, RollQualityDefectForm } from '../../../models/production/roll-quality-control';
import { RecentQcGroup, RollPayloadSearch } from '../../../models/rollsqc/rolls-search';
import { RollStatus } from '../../../models/production/roll-search';
import { WeightStationMessage } from '../../../models/mqtt/weight-station-message';
import { BarcodeScannerService } from '../../../services/barcode-scanner.service';
import { LocalHelperService } from '../../../services/local-helper.service';
import { EnumService } from '../../../services/enum.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AssetProxyService, AssetLite } from '../../../proxies/asset/asset.proxy.service';
import { QualityDefectProxyService } from '../../../proxies/inventory/quality-defect.proxy.service';
import { RollProxyService } from '../../../proxies/production/roll.proxy';
import { RollLabelProxyService } from '../../../proxies/production/roll-label.proxy.service';
import { WeightData } from '../../common/weight-control/weight-control.component';

@Component({
  selector: 'app-roll-qc-detail',
  templateUrl: './roll-qc-detail.component.html',
  styleUrl: './roll-qc-detail.component.scss',
})
export class RollQcDetailComponent implements OnInit {
  @ViewChild('focusButton', { static: false }) focusButton?: ElementRef<HTMLButtonElement>;

  private readonly ctr = inject(ChangeDetectorRef);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly qualityDefectProxyService = inject(QualityDefectProxyService);
  private readonly localHelperService = inject(LocalHelperService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly enumService = inject(EnumService);
  private readonly confirmDialogService = inject(ConfirmationService);
  private readonly rollLabelProxyService = inject(RollLabelProxyService);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly assetProxyService = inject(AssetProxyService);

  isLoading = signal(0);
  currentRoll = signal<Roll | null>(null);
  statusEnumObject: EnumObjectExtended = this.enumService.getEnum('RollStatus', 'production');
  statuses: EnumValues = this.statusEnumObject.values;
  allQualityDefects = signal<QualityDefect[]>([]);
  scaleData: WeightStationMessage | null = null;

  lastRecordsOfQC = signal<Roll[]>([]);
  qcMachines = signal<AssetLite[]>([]);

  approvedWeight = signal<WeightData | undefined>(undefined);
  rejectedWeight = signal<WeightData | undefined>(undefined);
  standWeight = signal<WeightData | undefined>(undefined);

  // Last stamp recorded at one of the QC machines loaded into qcMachines(), shown alongside
  // Stand Weight so QC can compare it against the reading on the scale.
  qcmWeight = computed(() => {
    const stationTimeStamps = this.currentRoll()?.stationTimeStamps;
    const qcmAssetIds = this.qcMachines()
      .map((m) => m.assetId)
      .filter((id): id is string => !!id);

    if (!stationTimeStamps || qcmAssetIds.length === 0) {
      return undefined;
    }

    return [...stationTimeStamps]
      .filter((s) => qcmAssetIds.includes(s.stationAssetId))
      .sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime())[0];
  });
  isNewRollApproved = signal(false);
  expectingPrint = false;

  files = signal<{ name: string; file: File }[]>([]);

  approvedRollNumber = '';
  rejectRollNumber = '';
  hasBeenScanned = signal(false);
  isLocationRestricted = signal(false);
  uniqueScanned: string[] = [];
  private alreadyValidated = false;

  private readonly WEIGHT_TOLERANCE = 0.01;
  protected readonly RollStatus = RollStatus;

  defectsViewOptions = [
    { label: 'Machine', value: 'machine' },
    { label: 'Operator', value: 'roll' },
  ];

  get rollProcess() {
    return (this.currentRoll()?.qualityControlLogs?.length ?? 0) > 0;
  }

  qualityDefectsTranslated = computed(() =>
    this.allQualityDefects().map((defect) => ({
      ...defect,
      name: this.localHelperService.getLocalizedValue(defect.name),
    })),
  );

  isRollProcessed = computed(() => {
    const roll = this.currentRoll();
    if (!roll || roll.status === undefined) return false;
    return roll.status !== RollStatus.NotInspected;
  });

  defectsView: FormControl<string> = new FormControl('machine', { nonNullable: true });
  selectedRoll: FormControl<Roll | null> = new FormControl({ value: null, disabled: !this.canEdit() });
  status: FormControl<number | null> = new FormControl({ value: null, disabled: true }, [Validators.required]);
  weightStationId: FormControl<string | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  initialWeight: FormControl<number | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  knittingMachine: FormControl<string | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  operatorFullName: FormControl<string | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  newQuality: FormControl<number | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  qualityDefectReference: FormControl<string | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  comments: FormControl<string | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );
  stationAssetId: FormControl<string | null> = new FormControl(
    { value: null, disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  );

  groupedComments = new FormArray<FormGroup<RollQualityDefectForm>>([]);

  constructor() {
    effect(() => {
      if (this.isRollProcessed() || !this.canEdit() || this.isLocationRestricted()) {
        this.status.disable({ emitEvent: false });
      } else {
        this.status.enable({ emitEvent: false });
      }
    });
  }

  canEdit() {
    if (AuthService.hasRole(UserGroup.RollQCAdmin) || AuthService.hasRole(UserGroup.RollQCEditor)) {
      return true;
    }
    if (AuthService.hasRole(UserGroup.RollQCViewer)) {
      return false;
    }
    return false;
  }

  private incrementLoading() {
    this.isLoading.update((value) => value + 1);
  }

  private decrementLoading() {
    this.isLoading.update((value) => Math.max(value - 1, 0));
  }

  private withLoading<T>() {
    this.incrementLoading();
    return finalize<T>(() => this.decrementLoading());
  }

  ngOnInit(): void {
    // csl-dropdown writes enum keys as strings; coerce back to number so all === comparisons work
    this.status.valueChanges.subscribe((value) => {
      if (value !== null && typeof value !== 'number') {
        this.status.setValue(Number(value), { emitEvent: false });
      }
    });

    this.selectedRoll.valueChanges.subscribe((value) => {
      if (value?.id) {
        if (this.alreadyValidated) {
          this.alreadyValidated = false;
          this.clearRollData();
          this.populateData(value);
        } else {
          this.clearRollData();
          this.rollProxyService
            .getByRollNumber(value.number!, false, '', false)
            .pipe(this.withLoading())
            .subscribe({
              next: ({ data }) => {
                if (data) {
                  this.populateData(data);
                } else {
                  this.selectedRoll.setValue(null, { emitEvent: false });
                }
              },
              error: () => {
                this.selectedRoll.setValue(null, { emitEvent: false });
              },
            });
        }
      } else {
        this.clearRollData();
      }
    });
    this.defectsView.valueChanges.subscribe(() => {
      this.ctr.detectChanges();
    });

    this.barcodeScannerService.barcode$.pipe(debounceTime(500)).subscribe((barcode) => {
      let lotSearchQuery = '';
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') {
        return;
      }
      if (barcode.includes('#')) {
        const cleanBarcode = barcode.split('#').pop() || '';
        lotSearchQuery = cleanBarcode;
      } else {
        lotSearchQuery = barcode;
      }

      this.attemptToGetRoll(lotSearchQuery);
    });
    this.getQualitiesDefectsTypes();
    this.getRecentQualityDefects();
    this.loadQcMachines();
  }

  public attemptToGetRoll(query: string) {
    this.rollProxyService
      .getByRollNumber(query, false, '', false)
      .pipe(this.withLoading())
      .subscribe({
        next: ({ data }) => {
          const findExactRoll = data && data.number?.toLowerCase() === query.toLowerCase() ? data : null;
          if (_.isNil(findExactRoll)) {
            GlobalMessageUtil.warn('Warn', 'Roll not found', 3000);
            return;
          }
          GlobalMessageUtil.success('Success', 'Roll found', 3000);
          this.alreadyValidated = true;
          this.selectedRoll.setValue(findExactRoll);
        },
      });
  }

  getQualitiesDefectsTypes() {
    this.qualityDefectProxyService
      .getQualitiesDefects()
      .pipe(this.withLoading())
      .subscribe(({ data }) => {
        if (data) {
          this.allQualityDefects.set(data);
        }
      });
  }

  loadQcMachines() {
    this.assetProxyService
      .filterByType(AssetTypeIds.rollQcMachine)
      .pipe(this.withLoading())
      .subscribe({
        next: ({ data }) => {
          if (data) {
            this.qcMachines.set(data);
          }
        },
      });
  }

  getQualityDefectByReference(reference: string) {
    const qd = this.allQualityDefects().find((defect) => defect.id === reference);
    return qd?.name.en ?? '';
  }

  getGroupedDefects() {
    const groupByMachine = this.defectsView.value === 'machine';
    const grouped = this.lastRecordsOfQC().reduce<Record<string, RecentQcGroup>>((acc, roll) => {
      const groupId = groupByMachine ? roll.assetId || 'Unknown Machine' : roll.createdBy || 'Unknown Operator';

      if (!acc[groupId]) {
        acc[groupId] = {
          groupId,
          rollNumbers: new Set<string>(),
          defects: [],
          totalDefectCount: 0,
        };
      }

      if (roll.number) {
        acc[groupId].rollNumbers.add(roll.number);
      }

      roll.qualityControlLogs?.forEach((qc) => {
        acc[groupId].defects.push({
          comments: qc.comments,
          timeStamp: qc.timeStamp,
          rollNumber: roll.number,
        });
        acc[groupId].totalDefectCount++;
      });

      return acc;
    }, {});

    return Object.values(grouped).map((group) => ({
      key: group.groupId,
      label: `${groupByMachine ? 'Machine' : 'Operator'}: ${group.groupId}`,
      ...(groupByMachine ? { assetId: group.groupId } : { createdBy: group.groupId }),
      rollNumbers: Array.from(group.rollNumbers).join(', '),
      defectCount: group.totalDefectCount,
      latestDefect: group.defects.at(-1) ?? null,
      allDefects: group.defects,
    }));
  }

  onDefectsViewChange() {
    this.ctr.detectChanges();
  }

  private getRecentQualityDefects() {
    const payload: RollPayloadSearch = {
      rollNumber: '',
      startDate: new Date(new Date().getTime() - 48 * 60 * 60 * 1000),
      endDate: new Date(),
    };
    this.rollProxyService
      .getRollsWithQCLast48Hours(payload)
      .pipe(this.withLoading())
      .subscribe({
        next: ({ data }) => {
          if (data) {
            this.lastRecordsOfQC.set(data);
          }
        },
      });
  }

  public fetchRolls = (query: string): Observable<Roll[]> => {
    if (!query) return of([]);

    return this.rollProxyService.getRollsByUnWeighed(query).pipe(map(({ data }) => data ?? []));
  };

  populateData(roll: Roll) {
    if (_.isEmpty(roll.stationTimeStamps)) {
      this.selectedRoll.setValue(null, { emitEvent: false });
      GlobalMessageUtil.warn('Unweighed Roll', 'Cannot Process Unweighed Roll');
      return;
    }

    const allowedLocations: string[] = [ImportantLocations.KNITTING, ImportantLocations.GREIGE];
    const isInAllowedLocation = !!roll.currentLocationCode && allowedLocations.includes(roll.currentLocationCode);
    const hasQCLogs = !!(roll.qualityControlLogs && roll.qualityControlLogs.length > 0);

    if (!isInAllowedLocation && !hasQCLogs) {
      const message =
        roll.currentLocationCode === ImportantLocations.IN_ROUTE
          ? 'Roll is currently in transit and cannot be processed for QC'
          : 'Roll must be at Knitting or Greige location to proceed with QC';
      GlobalMessageUtil.warn('Invalid Location', message);
    }

    this.status.setValue(roll.status ?? null);
    this.knittingMachine.setValue(roll.assetId ?? '');
    this.operatorFullName.setValue(roll.createdBy ?? '');
    this.weightStationId.setValue(roll.stationTimeStamps?.[0].stationAssetId ?? '');
    this.initialWeight.setValue(roll.stationTimeStamps?.[0]?.weightInLbs ?? 0);
    this.newQuality.setValue(roll.rollQuality ?? null);
    this.hasBeenScanned.set(hasQCLogs);

    if (hasQCLogs) {
      // Case 2: has prior QC → always readonly
      const latestQC = roll.qualityControlLogs![0];
      latestQC.qualityDefects.forEach((defect) => {
        const qualityDefect = this.allQualityDefects().find((qd) => qd.id === defect.qualityDefectReference);
        const name = qualityDefect ? this.localHelperService.getLocalizedValue(qualityDefect.name) : defect.qualityDefectReference;
        const group = new FormGroup<RollQualityDefectForm>({
          id: new FormControl<string | null>(defect.qualityDefectReference),
          name: new FormControl<string | null>(name ?? null),
          quantity: new FormControl<number | null>(defect.counter, [Validators.required, Validators.min(1)]),
          comment: new FormControl<string | null>(defect.comment ?? null),
        });
        this.qualityDefectReference.setValue(defect.qualityDefectReference);

        this.pushGroupedRollQualityDefect(group);
      });

      this.stationAssetId.setValue(latestQC.stationAssetId ?? null);
      this.comments.setValue(latestQC.comments ?? '');
      this.currentRoll.set(roll);

      this.newQuality.disable();
      this.qualityDefectReference.disable();
      this.comments.disable();
      this.stationAssetId.disable();
      this.groupedComments.disable();

      if (roll.status === RollStatus.PartiallyApproved) {
        const approvedRollNumber = latestQC.approvedRollNumber;
        const rejectedRollNumber = latestQC.rejectedRollNumber;
        this.approvedRollNumber = approvedRollNumber ?? '';
        this.rejectRollNumber = rejectedRollNumber ?? '';
        const rollToSearch = roll.number === approvedRollNumber ? rejectedRollNumber : approvedRollNumber;

        if (approvedRollNumber && rejectedRollNumber) {
          this.rollProxyService
            .getByRollNumbers([rollToSearch!])
            .pipe(this.withLoading())
            .subscribe({
              next: ({ data }) => {
                if (data) {
                  data.push(roll);
                  const approvedRoll = data.find((r) => r.number === approvedRollNumber);
                  const rejectedRoll = data.find((r) => r.number === rejectedRollNumber);

                  if (approvedRoll) {
                    const approvedWeightValue = approvedRoll.stationTimeStamps?.at(-1)?.weightInLbs ?? 0;
                    this.approvedWeight.set({ weight: approvedWeightValue, timestamp: new Date(), isManual: false });
                  }
                  if (rejectedRoll) {
                    const rejectedWeightValue = rejectedRoll.stationTimeStamps?.at(-1)?.weightInLbs ?? 0;
                    this.rejectedWeight.set({ weight: rejectedWeightValue, timestamp: new Date(), isManual: false });
                  }
                }
              },
            });
        } else {
          GlobalMessageUtil.error('Error', 'Roll QC data is incomplete. Please contact support.', 5000);
        }
      } else {
        this.standWeight.set({ weight: roll.stationTimeStamps?.at(-1)?.weightInLbs ?? 0, timestamp: new Date(), isManual: false });
      }
    } else if (isInAllowedLocation) {
      // Case 3: no prior QC + valid location → editable
      this.qualityDefectReference.setValue(null);
      this.comments.setValue('');

      this.newQuality.enable();
      this.qualityDefectReference.enable();
      this.comments.enable();
      this.stationAssetId.enable();
    } else {
      // Case 1: no prior QC + invalid location → readonly (warning already shown above)
      this.isLocationRestricted.set(true);
    }

    this.ctr.detectChanges();
  }

  clearRollData() {
    this.currentRoll.set(null);
    this.hasBeenScanned.set(false);
    this.isLocationRestricted.set(false);
    this.status.setValue(null);

    this.approvedWeight.set(undefined);
    this.rejectedWeight.set(undefined);
    this.standWeight.set(undefined);
    this.isNewRollApproved.set(false);

    this.knittingMachine.setValue('');
    this.operatorFullName.setValue('');
    this.weightStationId.setValue('');
    this.initialWeight.setValue(0);
    this.newQuality.setValue(null);
    this.qualityDefectReference.setValue(null);
    this.comments.setValue('');
    this.groupedComments.clear();

    this.newQuality.disable();
    this.qualityDefectReference.disable();
    this.comments.disable();
    this.stationAssetId.setValue(null);
    this.stationAssetId.disable();
    this.ctr.detectChanges();
  }

  private focusForBarcodeScanner() {
    setTimeout(() => {
      const el = this.focusButton?.nativeElement;
      if (el) {
        el.focus();
      }
    }, 100);
  }

  onSave() {
    if (this.groupedComments.invalid) {
      GlobalMessageUtil.error('Error', 'Please fill all the fields', 3000);
      this.groupedComments.markAllAsTouched();
      return;
    }

    if (_.isNil(this.status.value)) {
      GlobalMessageUtil.warn('Warn', 'Please select a valid status before submitting', 3000);
      return;
    }

    if (this.status.value === RollStatus.NotInspected) {
      GlobalMessageUtil.warn('Warn', 'Please select a valid status before submitting', 3000);
      return;
    }

    if (!this.stationAssetId.value) {
      GlobalMessageUtil.error('Error', 'Please select a QC machine before submitting', 3000);
      return;
    }

    if (!this.canSaveQualityControl()) {
      if (this.status.value === RollStatus.PartiallyApproved) {
        GlobalMessageUtil.error('Error', 'Please save both approved and rejected weights before submitting', 3000);
      } else {
        GlobalMessageUtil.error('Error', 'Please select a roll before submitting', 3000);
      }
      return;
    }

    if (!this.isWeightWithinTolerance()) {
      const original = this.initialWeight.value ?? 0;
      if (this.status.value === RollStatus.PartiallyApproved) {
        const sum = (this.approvedWeight()?.weight ?? 0) + (this.rejectedWeight()?.weight ?? 0);
        GlobalMessageUtil.error(
          'Weight Mismatch',
          `The sum of approved and rejected weights (${sum.toFixed(2)} lbs) must equal the original weight (${original.toFixed(2)} lbs) within ±${this.WEIGHT_TOLERANCE} lbs.`,
          6000,
        );
      } else {
        const recorded = this.standWeight()?.weight ?? 0;
        GlobalMessageUtil.error(
          'Weight Mismatch',
          `The recorded weight (${recorded.toFixed(2)} lbs) must equal the original weight (${original.toFixed(2)} lbs) within ±${this.WEIGHT_TOLERANCE} lbs.`,
          6000,
        );
      }
      return;
    }

    const dataForSend: RollQualityControlRequest = {
      weighStationId: this.weightStationId.value ?? '',
      rollId: this.selectedRoll.value?.id ?? '',
      weightInLbs: this.getFinalWeightForSave(),
      newRollStatus: this.status.value,
      qualityDefects: this.groupedComments.value.map((defect) => ({
        qualityDefectReference: defect.id ?? '',
        counter: defect.quantity ?? 0,
        comment: defect.comment ?? '',
      })),
      comments: this.comments.value ?? '',
      stationAssetId: this.stationAssetId.value ?? '',
    };

    const needsConfirmation = this.status.value === RollStatus.Rejected || this.status.value === RollStatus.PartiallyApproved;

    if (needsConfirmation) {
      const isPartial = this.status.value === RollStatus.PartiallyApproved;
      this.confirmDialogService.confirm({
        key: 'main',
        header: this.translateService.instant('RollQcDetail_ConfirmRejectHeader'),
        message: isPartial
          ? this.translateService.instant('RollQcDetail_ConfirmPartialRejectMessage')
          : this.translateService.instant('RollQcDetail_ConfirmRejectMessage'),
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: { label: this.translateService.instant('Common_Cancel'), variant: 'outlined' },
        acceptButtonProps: { label: this.translateService.instant('Common_Confirm') },
        accept: () => {
          this.submitQualityControl(dataForSend);
        },
      });
    } else {
      this.submitQualityControl(dataForSend);
    }
  }

  private submitQualityControl(dataForSend: RollQualityControlRequest): void {
    const newRollWeight = this.isNewRollApproved() ? this.approvedWeight() : this.rejectedWeight();

    this.rollProxyService
      .createWeightQualityControl(dataForSend, newRollWeight?.weight)
      .pipe(this.withLoading())
      .subscribe({
        next: ({ data }) => {
          if (data) {
            const roll = data.find((r) => r.id === this.selectedRoll.value?.id);
            this.selectedRoll.setValue(roll ?? null);
            this.hasBeenScanned.set(true);
            this.newQuality.disable();
            this.qualityDefectReference.disable();
            this.comments.disable();
            this.stationAssetId.disable();
            GlobalMessageUtil.success('Success', 'Roll quality control request created successfully.', 2000);
          }
        },
      });
  }

  onCancel() {
    this.resetValues();
    this.router.navigate(['/rolls-qc']);
  }

  uploadFile(event: Event, fileInput: HTMLInputElement): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      console.warn('No file selected.');
      return;
    }

    const file = input.files[0];

    if (this.selectedRoll && file) {
      const roll = this.selectedRoll.value!;
      const idRoll = roll.id ?? '';
      const idQC = roll.qualityControlLogs?.[0]?.id ?? '';

      if (!idRoll || !idQC) {
        GlobalMessageUtil.error('Error', 'Please save before de quality control for you can upload files', 3000);
        fileInput.value = '';
        return;
      }

      this.rollProxyService
        .uploadFileForQC(idRoll, idQC, file)
        .pipe(this.withLoading())
        .subscribe({
          next: ({ data }) => {
            if (data) {
              GlobalMessageUtil.success('Success', 'File uploaded successfully.', 2000);
              fileInput.value = '';
            }
          },
          error: () => {
            fileInput.value = '';
          },
        });
    }
  }

  printRoll(rollNumber: string) {
    this.rollLabelProxyService
      .getThermalLabelByRollNumber(rollNumber)
      .pipe(this.withLoading())
      .subscribe({
        next: (response) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          const iframe = document.createElement('iframe');
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.top = '-9999px';
          iframe.style.width = '0px';
          iframe.style.height = '0px';
          iframe.style.visibility = 'hidden';

          document.body.appendChild(iframe);

          iframe.onload = () => {
            try {
              setTimeout(() => {
                if (iframe.contentWindow) {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }
              }, 500);
            } catch (e) {
              console.error('Error printing PDF:', e);
              window.open(url, '_blank');
              setTimeout(() => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(url);
              }, 1000);
            }
          };
          this.expectingPrint = true;

          iframe.src = url;
          setTimeout(() => {
            if (this.expectingPrint) {
              this.expectingPrint = false;
              this.focusForBarcodeScanner();
            }
          }, 1000);
        },
        error: () => {
          this.expectingPrint = false;
        },
      });
  }

  getApprovedRollNumber(): string {
    if (this.hasBeenScanned()) {
      return this.approvedRollNumber;
    } else {
      return this.selectedRoll.value?.number ?? '';
    }
  }

  getRejectedRollNumber(): string {
    if (this.hasBeenScanned()) {
      return this.rejectRollNumber;
    } else {
      return '';
    }
  }

  private resetValues() {
    this.selectedRoll.setValue(null);
    this.status.setValue(0);
    this.weightStationId.setValue(null);
    this.initialWeight.setValue(0);
    this.knittingMachine.setValue(null);
    this.operatorFullName.setValue(null);
    this.newQuality.setValue(0);
    this.qualityDefectReference.setValue(null);
    this.comments.setValue('');
    this.stationAssetId.setValue(null);
    this.groupedComments.clear();

    this.approvedWeight.set(undefined);
    this.rejectedWeight.set(undefined);
    this.standWeight.set(undefined);
    this.isNewRollApproved.set(false);
  }

  onQualityDefectChange(event: SelectChangeEvent) {
    const selectedDefect = event.value;

    const qcDefect = this.allQualityDefects().find((def) => def.id == selectedDefect);

    if (qcDefect) {
      const exists = this.groupedComments.controls.some((ctrl) => ctrl.get('id')?.value === qcDefect.id);

      if (!exists) {
        const groupedDefect = this.createGroupedRollQualityDefect(qcDefect);
        this.pushGroupedRollQualityDefect(groupedDefect);
      } else {
        GlobalMessageUtil.warn('Warning', 'This defect has already been added.', 2000);
      }
    }
  }

  createGroupedRollQualityDefect(qualityDefect: QualityDefect, counter = 0, comment: string | null = null) {
    return new FormGroup<RollQualityDefectForm>({
      id: new FormControl<string | null>(qualityDefect.id ?? null),
      name: new FormControl<string | null>(this.localHelperService.getLocalizedValue(qualityDefect.name) ?? null),
      quantity: new FormControl<number | null>(counter, [Validators.required, Validators.min(1)]),
      comment: new FormControl<string | null>(comment),
    });
  }

  pushGroupedRollQualityDefect(group: FormGroup<RollQualityDefectForm>) {
    this.groupedComments.push(group);
  }

  deleteGroupedRollQualityDefect(index: number) {
    this.confirmDialogService.confirm({
      key: 'main',
      header: this.translateService.instant('RollQcDetail_ConfirmDeleteDefectHeader'),
      message: this.translateService.instant('RollQcDetail_ConfirmDeleteDefectMessage'),
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: { label: this.translateService.instant('Common_No'), variant: 'outlined' },
      acceptButtonProps: { label: this.translateService.instant('Common_Yes') },
      accept: () => {
        this.groupedComments.removeAt(index);
      },
    });
  }

  increaseValue(index: number) {
    const control = this.groupedComments.at(index).get('quantity');
    if (control) {
      const current = control.value ?? 0;
      control.setValue(current + 1);
    }
  }

  decreaseValue(index: number) {
    const control = this.groupedComments.at(index).get('quantity');
    if (control) {
      const current = control.value ?? 0;
      if (current > 1) {
        control.setValue(current - 1);
      }
    }
  }

  onApprovedWeightSaved(weightData: WeightData): void {
    this.approvedWeight.set(weightData);
    GlobalMessageUtil.success('Success', 'Approved weight saved locally', 2000);
  }

  onRejectedWeightSaved(weightData: WeightData): void {
    this.rejectedWeight.set(weightData);
    GlobalMessageUtil.success('Success', 'Rejected weight saved locally', 2000);
  }

  onApprovedWeightReset(): void {
    this.approvedWeight.set(undefined);
    GlobalMessageUtil.warn('Reset', 'Approved weight reset', 2000);
  }

  onRejectedWeightReset(): void {
    this.rejectedWeight.set(undefined);
    GlobalMessageUtil.warn('Reset', 'Rejected weight reset', 2000);
  }

  onStandWeightSaved(weightData: WeightData): void {
    this.standWeight.set(weightData);
    GlobalMessageUtil.success('Success', 'Stand weight saved locally', 2000);
  }

  onStandWeightReset(): void {
    this.standWeight.set(undefined);
    GlobalMessageUtil.warn('Reset', 'Stand weight reset', 2000);
  }

  onNewRollApprovedChange(isApproved: boolean): void {
    this.isNewRollApproved.set(isApproved);
  }

  getFinalWeightForSave(): number {
    if (this.status.value === RollStatus.PartiallyApproved) {
      return this.approvedWeight()?.weight || this.initialWeight.value || 0;
    } else {
      return this.standWeight()?.weight || this.initialWeight.value || 0;
    }
  }

  private isWeightWithinTolerance(): boolean {
    const original = this.initialWeight.value ?? 0;
    if (this.status.value === RollStatus.PartiallyApproved) {
      const sum = (this.approvedWeight()?.weight ?? 0) + (this.rejectedWeight()?.weight ?? 0);
      return Math.abs(sum - original) <= this.WEIGHT_TOLERANCE;
    }
    const recorded = this.standWeight()?.weight ?? 0;
    return Math.abs(recorded - original) <= this.WEIGHT_TOLERANCE;
  }

  get statusesForSelect(): EnumValues {
    if (this.rollProcess) {
      return {};
    }
    const keysToRemove = [String(RollStatus.NotInspected), String(RollStatus.OnHold)];
    const filteredStatuses = Object.fromEntries(Object.entries(this.statuses).filter(([key]) => !keysToRemove.includes(key)));
    return filteredStatuses;
  }

  canSaveQualityControl(): boolean {
    if (this.status.value === RollStatus.PartiallyApproved) {
      return !!(this.approvedWeight() && this.rejectedWeight());
    }
    return !!(this.selectedRoll.value && this.standWeight());
  }

  onQrCodeScanned(code: string): void {
    if (!this.uniqueScanned.includes(code)) {
      this.uniqueScanned.push(code);
    }
  }
}
